/**
 * "Text shape" for BoundedText: fits the text inside an arbitrary shape instead of the rectangle.
 *
 * The shape is a bitmap mask, and the layout only asks the mask which horizontal spans are free along
 * a band - so convex, concave (two spans on a band) and compound shapes all go through the same code.
 *
 * The mask is never rasterized here: the storefront API rasterizes each shape image once and
 * stores it as `<image>.mask.v1.json` (format in buildMaskFromRuns). Admin panel, storefront and
 * production files all read that file, so browsers and node-canvas rasterizing a shape differently
 * can't change where the lines break.
 * This file is kept identical in Turcos.App, TurcosJSLib and the PFP text-exporter (only the export
 * statement differs): the three must lay out the same lines.
 */

// Version of the mask files, the same the storefront API writes. Bumped when the rasterization changes
//  (resolution, threshold), which gives every shape a new file instead of mixing masks from both
const MASK_FORMAT_VERSION = 1;
// Steps a line advance is subdivided into when a band fits nothing: the band slides down to where
//  the shape opens up instead of losing a whole line. The last step lands on the next band anyway
const EMPTY_BAND_STEPS = 8;
// Precision of the font size search, relative to the size: the admin panel, the storefront and the
//  production files each lay the text out in a box of their own size, and an absolute precision (eg.
//  0.25px) stops the search at a different proportional size in each one - breaking lines elsewhere
const FONT_SIZE_TOLERANCE = 0.005;
// Lowest font size the search tries, relative to the max for the same reason: an absolute floor
//  (eg. 1px) starts the search at a different proportion in each box
const MIN_FONT_SIZE_RATIO = 0.005;
const EPSILON = 1e-6;
// A mask holds ~1.3MB of pixel data plus its guide canvas, so the cache is capped. Evicting is
//  safe: an element keeps its own reference (item(0)._shapeMask), the entry is only rebuilt if the
//  shape is picked again. Above the shapes a single template applies at once
const MASK_CACHE_SIZE = 12;

const maskCache = new Map();

/** Least recently used first, which is what makes the Map's insertion order the eviction order */
function readMaskCache(cacheKey) {
    if (!maskCache.has(cacheKey)) {
        return undefined;
    }
    const mask = maskCache.get(cacheKey);
    maskCache.delete(cacheKey);
    maskCache.set(cacheKey, mask);
    return mask;
}

function writeMaskCache(cacheKey, mask) {
    maskCache.set(cacheKey, mask);
    if (maskCache.size > MASK_CACHE_SIZE) {
        maskCache.delete(maskCache.keys().next().value);
    }
}

/**
 * Builds a mask from a flat "inside" grid, cropped to the bounds of its inside pixels so padding
 * around the shape (common in uploaded images) doesn't shrink the fit. Canvas-free so it can be
 * unit tested.
 * @param {Uint8Array|number[]} inside row-major grid of width * height, truthy = inside the shape
 * @param {number} width
 * @param {number} height
 * @returns {Object|null} mask exposing only its size and its queries, or null when no pixel is
 *  inside
 */
function buildMask(inside, width, height) {
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (inside[y * width + x]) {
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
        }
    }
    if (maxX < 0) {
        return null;
    }

    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const cropped = new Uint8Array(w * h);
    // outsidePrefix[y * w + x] = outside pixels in column x within rows [0, y), so checking whether
    //  a column is free along any band is a single subtraction
    const outsidePrefix = new Int32Array((h + 1) * w);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const isInside = inside[(y + minY) * width + x + minX] ? 1 : 0;
            cropped[y * w + x] = isInside;
            outsidePrefix[(y + 1) * w + x] = outsidePrefix[y * w + x] + 1 - isInside;
        }
    }

    let guideCanvas = null;

    // The pixels stay in this closure instead of being properties of the mask: as properties, two
    //  ~260k entry typed arrays hang off a canvas element, and every generic deep walker that
    //  reaches that element walks them index by index on every mouse move - Vue's deep watcher over
    //  the canvas (_traverse), lodash's cloneDeep in the drag and undo paths, angular.copy. Being
    //  unreachable stops all of them, where Object.freeze only stopped Vue's; a copied mask still
    //  works, since a clone keeps the same function references
    return Object.freeze({
        width: w,
        height: h,

        /**
         * Horizontal spans free along the WHOLE band [y0, y1] (box coordinates, top-left origin),
         * left to right. Rows are rounded outwards so a span never covers a pixel outside the shape.
         * @param {{scale: number, offsetX: number, offsetY: number}} fit see fitMask
         * @returns {{x: number, width: number}[]}
         */
        getBandSlots(fit, y0, y1, inset = 0, minWidth = 0) {
            const r0 = Math.floor((y0 - fit.offsetY) / fit.scale + EPSILON);
            const r1 = Math.max(Math.ceil((y1 - fit.offsetY) / fit.scale - EPSILON), r0 + 1);
            if (r0 < 0 || r1 > h) {
                return [];
            }

            const slots = [];
            let runStart = -1;
            for (let x = 0; x <= w; x++) {
                const free = x < w && outsidePrefix[r1 * w + x] - outsidePrefix[r0 * w + x] === 0;
                if (free && runStart < 0) {
                    runStart = x;
                } else if (!free && runStart >= 0) {
                    const left = fit.offsetX + runStart * fit.scale + inset;
                    const right = fit.offsetX + x * fit.scale - inset;
                    if (right > left && right - left >= minWidth) {
                        slots.push({ x: left, width: right - left });
                    }
                    runStart = -1;
                }
            }
            return slots;
        },

        /**
         * Canvas with the mask's inside pixels filled, drawn over the object as an editor-only
         * guide. It is painted with "multiply" (see BoundedText.drawBorders), so this is the color
         * the guide ends up having over a white area.
         */
        getGuideCanvas() {
            if (!guideCanvas) {
                guideCanvas = fabric.util.createCanvasElement();
                guideCanvas.width = w;
                guideCanvas.height = h;
                const ctx = guideCanvas.getContext('2d');
                const imageData = ctx.createImageData(w, h);
                for (let i = 0; i < cropped.length; i++) {
                    if (cropped[i]) {
                        imageData.data.set([0xed, 0xe9, 0xfb, 255], i * 4);
                    }
                }
                ctx.putImageData(imageData, 0, 0);
            }
            return guideCanvas;
        }
    });
}

/**
 * Where the mask lands inside a box of the given size: scaled uniformly as big as possible
 * (keeps the shape's proportions) and centered.
 */
function fitMask(mask, boxWidth, boxHeight) {
    const scale = Math.min(boxWidth / mask.width, boxHeight / mask.height);
    const width = mask.width * scale;
    const height = mask.height * scale;
    return { scale, width, height, offsetX: (boxWidth - width) / 2, offsetY: (boxHeight - height) / 2 };
}

/** @see the mask's own getBandSlots */
function getBandSlots(mask, fit, y0, y1, inset = 0, minWidth = 0) {
    return mask.getBandSlots(fit, y0, y1, inset, minWidth);
}

/**
 * Greedy word wrap into the shape: bands top to bottom, free spans left to right within each band.
 * Words are never broken - a span too narrow for the next word is skipped - and "\n" continues on
 * the next band. A band that fits nothing doesn't consume a line: it slides down in small steps
 * until the shape is wide enough, so curved tops (a heart's lobes, a circle's crown) don't lose a
 * whole line to the narrow rows above the first one that fits.
 * @param {Object} options
 * @param {string} options.text
 * @param {(line: string) => number} options.measure rendered width of a line, stroke included
 * @param {(y0: number, y1: number) => {x: number, width: number}[]} options.getSlots
 * @param {number} options.lineAdvance vertical distance between consecutive bands
 * @param {number} options.bandHeight height each band must be free along (glyph box + stroke)
 * @param {number} options.startY top of the first band
 * @param {number} options.endY bottom limit
 * @returns {{lines: {text: string, x: number, width: number, y: number, endsParagraph: boolean}[], fits: boolean, bottom: number}}
 */
function layoutTextInShape({ text, measure, getSlots, lineAdvance, bandHeight, startY, endY }) {
    const paragraphs = text
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((paragraph) => paragraph.split(' ').filter(Boolean));
    const lines = [];
    let y = startY;
    let bottom = startY;

    // With a NaN or infinite box no band ever reaches endY: the loop below wouldn't end
    if (!(lineAdvance > 0) || !isFinite(startY + endY + bandHeight)) {
        return { lines, fits: false, bottom };
    }

    for (const words of paragraphs) {
        if (!words.length) {
            // Blank line: only takes vertical space
            y += lineAdvance;
            continue;
        }

        let wordIndex = 0;
        while (wordIndex < words.length) {
            if (y + bandHeight > endY + EPSILON) {
                return { lines, fits: false, bottom };
            }

            const lineCount = lines.length;
            for (const slot of getSlots(y, y + bandHeight)) {
                if (wordIndex >= words.length) {
                    break;
                }
                let line = words[wordIndex];
                if (measure(line) > slot.width) {
                    continue;
                }
                wordIndex++;
                while (wordIndex < words.length) {
                    const candidate = line + ' ' + words[wordIndex];
                    if (measure(candidate) > slot.width) {
                        break;
                    }
                    line = candidate;
                    wordIndex++;
                }
                lines.push({ text: line, x: slot.x, width: slot.width, y, endsParagraph: false });
                bottom = y + bandHeight;
            }
            y += lines.length > lineCount ? lineAdvance : lineAdvance / EMPTY_BAND_STEPS;
        }
        lines[lines.length - 1].endsParagraph = true;
    }

    return { lines, fits: true, bottom };
}

/**
 * Mask from a mask file `{ v, width, height, rows }`: the shape cropped to its bounds, each row as
 * the [start, end) pairs of its inside runs, left to right.
 * @returns {Object} mask, same as buildMask
 */
function buildMaskFromRuns(data) {
    if (!data || data.v !== MASK_FORMAT_VERSION || !(data.width > 0) || !(data.height > 0) || !Array.isArray(data.rows)) {
        throw new Error('Invalid text shape mask file');
    }
    const { width, height, rows } = data;
    const inside = new Uint8Array(width * height);
    for (let y = 0; y < height && y < rows.length; y++) {
        const row = rows[y];
        for (let i = 0; i + 1 < row.length; i += 2) {
            inside.fill(1, y * width + Math.max(0, row[i]), y * width + Math.min(width, row[i + 1]));
        }
    }
    const mask = buildMask(inside, width, height);
    if (!mask) {
        throw new Error('The text shape mask file has no shape to fit the text into');
    }
    return mask;
}

/** Url of the mask file of a shape image url (`<image>.mask.v1.json`), keeping its query string */
function getMaskUrl(imageUrl) {
    const queryIndex = imageUrl.indexOf('?');
    const base = queryIndex < 0 ? imageUrl : imageUrl.slice(0, queryIndex);
    const query = queryIndex < 0 ? '' : imageUrl.slice(queryIndex);
    return `${base}.mask.v${MASK_FORMAT_VERSION}.json${query}`;
}

/**
 * Mask cached under `cacheKey`, built from the mask file `fetchRuns` resolves the first time. Concurrent
 * and later requests share the same promise; a failed one is dropped so the next request retries.
 * @param {string} cacheKey usually the shape image url
 * @param {() => Promise<Object>} fetchRuns resolves the parsed mask file
 * @returns {Promise<Object>} mask
 */
function loadMask(cacheKey, fetchRuns) {
    const cached = readMaskCache(cacheKey);
    if (cached !== undefined) {
        return cached;
    }
    const request = Promise.resolve()
        .then(fetchRuns)
        .then(buildMaskFromRuns);
    request.catch(() => {
        if (maskCache.get(cacheKey) === request) {
            maskCache.delete(cacheKey);
        }
    });
    writeMaskCache(cacheKey, request);
    return request;
}

/** @see the mask's own getGuideCanvas */
function getMaskGuideCanvas(mask) {
    return mask.getGuideCanvas();
}

/**
 * layoutTextInShape anchored at the center (or bottom) of [startY, endY]: uses the fewest bands the
 * text fits in and places that block centered (or bottom aligned), so the text grows outwards from
 * the anchor as lines are added. 'top' is plain layoutTextInShape.
 * @param {Object} options layoutTextInShape options plus `anchor` ('top' | 'center' | 'bottom')
 */
function layoutTextInShapeAnchored(options) {
    const { anchor, lineAdvance, bandHeight, startY, endY } = options;
    if ((anchor !== 'center' && anchor !== 'bottom') || !(lineAdvance > 0)) {
        return layoutTextInShape(options);
    }

    const maxBands = Math.floor((endY - startY - bandHeight) / lineAdvance + EPSILON) + 1;
    if (maxBands < 1) {
        return { lines: [], fits: false, bottom: startY };
    }

    const layoutBands = (bandCount) => {
        const blockHeight = (bandCount - 1) * lineAdvance + bandHeight;
        const top = anchor === 'center' ? (startY + endY - blockHeight) / 2 : endY - blockHeight;
        return layoutTextInShape({ ...options, startY: top, endY: top + blockHeight });
    };

    let result = layoutBands(maxBands);
    if (!result.fits) {
        return result;
    }
    // Fewest bands that fit - more bands only add room, so binary search
    let lo = 1;
    let hi = maxBands;
    while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midResult = layoutBands(mid);
        if (midResult.fits) {
            hi = mid;
            result = midResult;
        } else {
            lo = mid + 1;
        }
    }
    return result;
}

/**
 * Keeps the spans of a band at least `minGap` apart: where two of them are closer (eg. the two lobes of a
 * heart right where they meet), their inner edges shrink until the gap is `minGap`. Otherwise the last word
 * of one span and the first word of the next one end up glued ("bienHola").
 * @param {{x: number, width: number}[]} slots spans of a band, left to right (see getBandSlots)
 * @param {number} minGap usually the width of a space at the current font size
 * @returns {{x: number, width: number}[]}
 */
function separateSlots(slots, minGap) {
    if (slots.length < 2) {
        return slots;
    }
    const separated = slots.map((slot) => ({ x: slot.x, width: slot.width }));
    for (let i = 0; i + 1 < separated.length; i++) {
        const left = separated[i];
        const right = separated[i + 1];
        const gap = right.x - (left.x + left.width);
        if (gap < minGap) {
            const shrink = (minGap - gap) / 2;
            left.width -= shrink;
            right.x += shrink;
            right.width -= shrink;
        }
    }
    return separated.filter((slot) => slot.width > 0);
}

/**
 * The largest single font size whose layout fits the whole text inside the shape, and that layout.
 * Everything is proportional to the box: the same text in a box k times bigger fits at a k times bigger
 * font size with the same lines, which is what keeps admin panel, storefront and production files alike.
 * When not even the minimum size fits, keeps the longest prefix that does, as the rectangular fit does.
 * @param {Object} options
 * @param {string} options.text
 * @param {Object} options.mask see buildMaskFromRuns
 * @param {number} options.boxWidth
 * @param {number} options.boxHeight
 * @param {(line: string) => number} options.measure width of a line at font size 1, stroke excluded
 * @param {number} options.lineAdvance distance between consecutive lines at font size 1
 * @param {number} options.glyphHeight height of the glyph box (ascender to descender) at font size 1
 * @param {number} options.strokeWidth
 * @param {number} options.minFontSize
 * @param {number} options.maxFontSize
 * @param {string} options.anchor vertical align, see layoutTextInShapeAnchored
 * @returns {{fontSize: number, lines: {text: string, x: number, width: number, y: number, endsParagraph: boolean}[], deletedChars: boolean}}
 */
function fitTextInShape({
    text,
    mask,
    boxWidth,
    boxHeight,
    measure,
    lineAdvance,
    glyphHeight,
    strokeWidth,
    minFontSize,
    maxFontSize,
    anchor
}) {
    const fit = fitMask(mask, boxWidth, boxHeight);
    const shapeTop = fit.offsetY;
    const shapeBottom = fit.offsetY + fit.height;

    // Every pass of the search re-measures the same lines, only the scale changes
    const lineWidths = new Map();
    const measureCached = (line) => {
        let width = lineWidths.get(line);
        if (width === undefined) {
            width = measure(line);
            lineWidths.set(line, width);
        }
        return width;
    };
    // A space at font size 1, what the words of two spans of the same band are kept apart by
    const spaceWidth = measureCached('a a') - measureCached('aa');

    const layout = (layoutText, fontSize) =>
        layoutTextInShapeAnchored({
            text: layoutText,
            measure: (line) => measureCached(line) * fontSize + strokeWidth,
            getSlots: (y0, y1) => separateSlots(getBandSlots(mask, fit, y0, y1), spaceWidth * fontSize),
            lineAdvance: lineAdvance * fontSize,
            bandHeight: glyphHeight * fontSize + strokeWidth,
            startY: shapeTop,
            endY: shapeBottom,
            anchor
        });

    const min = Math.max(Math.min(minFontSize, maxFontSize), maxFontSize * MIN_FONT_SIZE_RATIO);
    let fontSize = maxFontSize;
    let result = layout(text, maxFontSize);
    let deletedChars = false;

    if (!result.fits) {
        const minResult = layout(text, min);
        if (minResult.fits) {
            let lo = min;
            let hi = maxFontSize;
            fontSize = min;
            result = minResult;
            while (hi - lo > lo * FONT_SIZE_TOLERANCE) {
                const mid = (lo + hi) / 2;
                const midResult = layout(text, mid);
                if (midResult.fits) {
                    lo = mid;
                    fontSize = mid;
                    result = midResult;
                } else {
                    hi = mid;
                }
            }
        } else {
            let lo = 0;
            let hi = text.length;
            result = layout('', min);
            while (lo < hi) {
                const mid = Math.ceil((lo + hi) / 2);
                const midResult = layout(text.slice(0, mid), min);
                if (midResult.fits) {
                    lo = mid;
                    result = midResult;
                } else {
                    hi = mid - 1;
                }
            }
            fontSize = min;
            deletedChars = lo < text.length;
        }
    }

    const lines = result.lines.length
        ? result.lines
        : [{ text: '', x: 0, width: boxWidth, y: shapeTop, endsParagraph: true }];
    return { fontSize, lines, deletedChars };
}

module.exports = {
    MASK_FORMAT_VERSION,
    buildMask,
    buildMaskFromRuns,
    getMaskUrl,
    loadMask,
    fitMask,
    getBandSlots,
    layoutTextInShape,
    layoutTextInShapeAnchored,
    separateSlots,
    fitTextInShape,
    getMaskGuideCanvas
};
